import { format } from 'date-fns'
import { groupBy, map, sortBy, uniq } from 'lodash'

import { ADDITION_SUFFIX, MAIN_DATA_KEY, X_AXIS_LABEL_KEY } from 'consts/chart'
import { BLANK_VALUE } from 'consts/ui'
import { ChartData, ChartDataValues, ChartItem, DataKey, StatisticBase } from 'types'

import { formatBlankLabel } from '../formatBlankLabel'

import { getKeyBase } from './getKeyBase'

type ChartConfiguration = {
  chartData: ChartData
  charts: ChartItem[]
}

const getKey = (key: string, isAddition: boolean) =>
  isAddition ? `${key}${ADDITION_SUFFIX}` : key
const preInitialiseValuesWithZeroes = (dataKeySet: Set<string>) =>
  [...dataKeySet].reduce((acc, key) => {
    acc[key] = {
      value: 0,
      hasData: false,
    }

    return acc
  }, {})
const sortWithBlankAtEnd = (list: string[], isAddition = false) =>
  sortBy(list, name => name === getKey(BLANK_VALUE, isAddition))

export const prepareChartData = <T extends StatisticBase, K extends keyof T>({
  data,
  dataKey,
  dataCountKey,
  labelTimeFormat = 'HH:mm',
  colorMap,
  isAddition = false,
}: {
  data: T[]
  dataKey: DataKey<T, K>
  dataCountKey: K
  colorMap: Record<string, string>
  labelTimeFormat?: string
  isAddition?: boolean
}): ChartConfiguration => {
  const dataKeySet = new Set(
    sortWithBlankAtEnd(
      data?.map(item => getKey(getKeyBase(item, dataKey), isAddition)),
      isAddition,
    ),
  )
  const groupedRecords = groupBy(data, 'date')

  const unifiedRecords = Object.entries(groupedRecords).map(
    ([date, statisticsGroupByDate]) => {
      const timestamp = Number(date)
      const values = statisticsGroupByDate.reduce<ChartDataValues>((acc, current) => {
        const key = getKey(getKeyBase(current, dataKey), isAddition)
        const value = current[dataCountKey]

        if (typeof value === 'number') {
          acc[key] = { value, hasData: true }
        } else {
          throw new Error('The value of `data[dataCountKey]` should be a number')
        }

        return acc
      }, preInitialiseValuesWithZeroes(dataKeySet))

      return {
        date: timestamp,
        [MAIN_DATA_KEY]: values,
        [X_AXIS_LABEL_KEY]: format(timestamp, labelTimeFormat),
      }
    },
  )
  const sortedRecords = sortBy(unifiedRecords, 'date')

  const uniqueValueNames = uniq(map(data, item => getKeyBase(item, dataKey)))
  const sortedBlankNames = sortWithBlankAtEnd(uniqueValueNames)
  const charts = sortedBlankNames.map(valueName => ({
    name: formatBlankLabel(String(valueName)),
    value: getKey(String(valueName), isAddition),
    color: colorMap[String(valueName)],
  }))

  return {
    charts,
    chartData: sortedRecords,
  }
}
